/**
 * Manages transitions between scroll mode and video mode.
 * Handles animations and state changes for entering and exiting scroll mode.
 */
import gsap        from 'gsap';
import gsapManager from './GSAPManager.js';

class ScrollModeManager {
    constructor({
        scrollSectionElement,
        videoStageElement,
        experienceRootElement,
        videoExperience
    }) {
        this.scrollSectionElement  = scrollSectionElement;
        this.videoStageElement     = videoStageElement;
        this.experienceRootElement = experienceRootElement;
        this.videoExperience       = videoExperience;
        this.heroShellElement      = videoStageElement
            ? videoStageElement.querySelector('.experience-hero__video-shell')
            : null;
        this.isMobile             = ScrollModeManager.detectMobile();

        // Constants
        this.SCROLL_SECTION_OVERLAY_DELAY = 5;

        this.sectionOneScrollTimeline = null;
    }

    static detectIOS() {
        if (typeof navigator === 'undefined') {
            return false;
        }

        const userAgent   = navigator.userAgent || navigator.vendor || '';
        const isIOSDevice = /iPad|iPhone|iPod/.test(userAgent);

        const isTouchMac = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;

        return isIOSDevice || isTouchMac;
    }

    static detectMobile() {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
            return false;
        }

        return window.matchMedia('(max-width: 1080px)').matches;
    }

    /**
     * Enter scroll mode with animation.
     */
    enterScrollMode(onComplete) {
        if (!this.videoExperience || !this.scrollSectionElement) {
            return;
        }

        this.videoExperience.videoToScrollEnd();

        if (this.isMobile && this.heroShellElement) {
            this.heroShellElement.classList.add('experience-hero__video-shell--scroll-zoom');
        }

        if (this.experienceRootElement) {
            this.experienceRootElement.classList.add('experience--scroll-mode');
        }

        // Lock page scroll while overlay animation is running
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow            = 'hidden';

        // Prepare scroll section for overlay entrance
        this.scrollSectionElement.classList.remove('experience__scroll-section--hidden');
        this.scrollSectionElement.classList.add('experience__scroll-section--overlay');

        // Start overlay slide-in animation via GSAP with configurable delay
        gsapManager.killTweensOf(this.scrollSectionElement);

        gsap.fromTo(
            this.scrollSectionElement,
            {
                opacity : 0
            },
            {
                opacity  : 1,
                duration : 1,
                delay    : this.SCROLL_SECTION_OVERLAY_DELAY,
                ease     : 'sine.inOut',
                onStart  : () => {
                    this.scrollSectionElement.classList.add('experience__scroll-section--overlay-visible');
                },
                onComplete : () => {
                    this.scrollSectionElement.classList.remove(
                        'experience__scroll-section--overlay',
                        'experience__scroll-section--overlay-visible'
                    );

                    if (this.videoStageElement) {
                        this.videoStageElement.classList.add('experience__video-stage--hidden');
                    }

                    document.documentElement.style.overflow = '';
                    document.body.style.overflow            = '';

                    gsap.set(this.scrollSectionElement, {
                        clearProps : 'transform,opacity'
                    });

                    if (typeof onComplete === 'function') {
                        onComplete();
                    }
                }
            }
        );

        if (this.heroShellElement) {
            gsapManager.killTweensOf(this.heroShellElement);

            gsap.to(
                this.heroShellElement,
                {
                    filter   : 'blur(8px)',
                    scale    : 0.96,
                    duration : 1,
                    delay    : this.SCROLL_SECTION_OVERLAY_DELAY,
                    ease     : 'sine.inOut'
                }
            );
        }
    }

    /**
     * Exit scroll mode and return to video.
     */
    exitScrollMode(onComplete) {
        if (!this.videoExperience || !this.scrollSectionElement || !this.videoStageElement) {
            return;
        }

        // Ensure scroll is at the top before returning to video
        window.scrollTo(0, 0);

        // Show video stage behind scroll section
        this.videoStageElement.classList.remove('experience__video-stage--hidden');

        if (this.experienceRootElement) {
            this.experienceRootElement.classList.remove('experience--scroll-mode');
        }

        // Start video reverse back to phase two
        this.videoExperience.reverseFromScrollModeToPhaseTwo();

        if (this.heroShellElement) {
            gsapManager.killTweensOf(this.heroShellElement);

            gsap.to(
                this.heroShellElement,
                {
                    filter   : 'blur(0px)',
                    scale    : 1,
                    duration : 0.8,
                    ease     : 'sine.out'
                }
            );

            if (this.isMobile) {
                this.heroShellElement.classList.remove('experience-hero__video-shell--scroll-zoom');
            }
        }

        // Prepare scroll section for overlay exit animation
        this.scrollSectionElement.classList.remove('experience__scroll-section--hidden');
        this.scrollSectionElement.classList.add(
            'experience__scroll-section--overlay',
            'experience__scroll-section--overlay-visible'
        );

        gsapManager.killTweensOf(this.scrollSectionElement);

        // Animate scroll section out while video rewinds
        gsap.to(
            this.scrollSectionElement,
            {
                yPercent   : 100,
                opacity    : 0,
                duration   : 1,
                ease       : 'sine.inOut',
                onComplete : () => {
                    this.scrollSectionElement.classList.remove(
                        'experience__scroll-section--overlay',
                        'experience__scroll-section--overlay-visible'
                    );
                    this.scrollSectionElement.classList.add('experience__scroll-section--hidden');

                    gsap.set(this.scrollSectionElement, {
                        clearProps : 'transform,opacity'
                    });

                    if (this.sectionOneScrollTimeline) {
                        if (this.sectionOneScrollTimeline.scrollTrigger) {
                            this.sectionOneScrollTimeline.scrollTrigger.kill();
                        }

                        this.sectionOneScrollTimeline.kill();
                        this.sectionOneScrollTimeline = null;
                    }

                    if (typeof onComplete === 'function') {
                        onComplete();
                    }
                }
            }
        );
    }

    /**
     * Cleanup.
     */
    destroy() {
        // Kill all GSAP animations
        gsapManager.killTweensOf(this.scrollSectionElement);
        gsapManager.killTweensOf(this.heroShellElement);
        gsapManager.killTweensOf(this.videoStageElement);
        gsapManager.killTweensOf(this.experienceRootElement);

        // Clean up timeline
        if (this.sectionOneScrollTimeline) {
            this.sectionOneScrollTimeline.kill();
            this.sectionOneScrollTimeline = null;
        }

        // Reset element references
        this.scrollSectionElement = null;
        this.videoStageElement = null;
        this.experienceRootElement = null;
        this.heroShellElement = null;
        this.videoExperience = null;
    }
}

export default ScrollModeManager;
